
import React, { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useSessionStore } from '@/store/sessionStore';
import { useContextSnippetStore } from '@/store/contextSnippetStore';
import { toast } from 'sonner';
import { Send, Pencil, Minimize2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import AdHocContextPills from './AdHocContextPills';

interface PromptInputProps {
    prompt: string;
    setPrompt: React.Dispatch<React.SetStateAction<string>>;
    handlePromptKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const PromptInput = React.forwardRef<HTMLTextAreaElement, PromptInputProps>(({
    prompt,
    setPrompt,
    handlePromptKeyDown,
}, ref) => {
    const {
        currentSessionId,
        currentSessionStatus,
        currentSessionDetails,
        isLoading,
        submitPrompt,
        retrySessionInput,
        setActiveMainPage,
        executionStrategy,
        quoteText,
        setQuoteText,
        lastSubmittedPrompt,
        editingRetryInputId,
        clearRetryMode,
    } = useSessionStore();
    const { snippets: contextSnippets } = useContextSnippetStore();

    useEffect(() => {
        if (quoteText) {
            let formattedText = '';
            if (quoteText.type === 'markdown') {
                formattedText = quoteText.text.split('\n').map(line => `> ${line}`).join('\n');
            } else {
                // For 'code', text is already pre-formatted
                formattedText = quoteText.text;
            }

            setPrompt(prev => `${prev}${prev ? '\n\n' : ''}${formattedText}\n\n`);
            
            if (typeof ref === 'object' && ref?.current) {
                ref.current.focus();
                setTimeout(() => {
                    if (ref.current) {
                        ref.current.scrollTop = ref.current.scrollHeight;
                    }
                }, 0);
            }
            // Reset the quote text in the store
            setQuoteText(null);
        }
    }, [quoteText, setPrompt, setQuoteText, ref]);

    const handleSubmit = async () => {
        if (!currentSessionId) {
            toast.error("No active session. Please start a new session.");
            return;
        }
        if (!prompt.trim()) return;

        let success = false;
        
        if (editingRetryInputId) {
            // Retry mode: update existing session input
            success = await retrySessionInput(editingRetryInputId, {
                user_prompt: prompt,
                execution_strategy: executionStrategy,
            });
        } else {
            // Normal mode: create new session input
            success = await submitPrompt({
                user_prompt: prompt,
                execution_strategy: executionStrategy,
            });
        }

        if (success) {
            setPrompt("");
            clearRetryMode();
            setActiveMainPage('plan');
        }
    };

    const handleCompress = async () => {
        if (!currentSessionId) {
            toast.error("No active session.");
            return;
        }
        const compressSnippet = contextSnippets.find(s => s.handle === 'compress');
        if (!compressSnippet) {
            toast.error("Compress context snippet not found. Make sure it exists.");
            return;
        }
        const success = await submitPrompt({
            user_prompt: compressSnippet.template_content,
            execution_strategy: executionStrategy,
        });
        if (success) {
            setActiveMainPage('plan');
        }
    };

    const isTextareaDisabled =
        !currentSessionId ||
        currentSessionStatus !== 'active' ||
        isLoading;

    const isSubmitButtonDisabled = !prompt || !prompt.trim() || isTextareaDisabled;

    const handleLocalKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Tab' && !prompt && lastSubmittedPrompt) {
            e.preventDefault();
            setPrompt(lastSubmittedPrompt);
            return;
        }

        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (isSubmitButtonDisabled) return;
            handleSubmit();
        } else {
            handlePromptKeyDown(e);
        }
    };

    const defaultPlaceholder = "Type your prompt here... (use @ for files, # for folders)";
    const isRetryMode = editingRetryInputId !== null;
    const placeholder = isRetryMode
        ? "Edit your prompt and click Retry..."
        : lastSubmittedPrompt
        ? `Press Tab: ${lastSubmittedPrompt.replace(/\n/g, ' ').substring(0, 50)}${lastSubmittedPrompt.length > 50 ? '...' : ''}   (${defaultPlaceholder})`
        : defaultPlaceholder;

    return (
        <div className="flex-shrink-0 p-1 border-t border-slate-200 dark:border-slate-700 max-h-[50vh] overflow-y-auto">
            {isRetryMode && (
                <div className="flex items-center justify-between mb-2 px-2">
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                            <Pencil className="mr-1 h-3 w-3" />
                            Editing previous prompt
                        </Badge>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => {
                                clearRetryMode();
                                setPrompt("");
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
            <AdHocContextPills />

            <div className="flex items-end gap-1">
                <Textarea
                    ref={ref}
                    placeholder={placeholder}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleLocalKeyDown}
                    className="flex-grow resize-none"
                    rows={3}
                    disabled={isTextareaDisabled}
                />
                <TooltipProvider>
                    <div className="flex flex-col gap-1">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={handleCompress}
                                    disabled={!currentSessionId || isLoading}
                                >
                                    <Minimize2 className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Compress Context</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="icon"
                                    onClick={handleSubmit}
                                    disabled={isSubmitButtonDisabled}
                                >
                                    {isLoading ? (
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{isRetryMode ? 'Retry' : 'Send'} (⌘+Enter)</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            </div>
        </div>
    );
});

PromptInput.displayName = 'PromptInput';

export default PromptInput;